// Image banner slideshow — extends the theme's SlideshowComponent with:
//  - seamless forward wrap-around via the .ibs-clone duplicate slide
//    (rendered by the section when there is more than one slide)
//  - permanent autoplay stop on any manual interaction
//  - click-to-copy promo chips
// Loads after global.js (both deferred), so SlideshowComponent exists.

if (typeof SlideshowComponent !== 'undefined' && !customElements.get('image-banner-slideshow')) {
  customElements.define(
    'image-banner-slideshow',
    class ImageBannerSlideshow extends SlideshowComponent {
      constructor() {
        super();
        this.ibsClone = this.querySelector('.ibs-clone');
        this.ibsSkipSnap = false;

        // The clone is decorative: nothing inside it may take focus.
        this.ibsClone?.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));

        const stop = () => {
          if (this.autoplayButtonIsSetToPlay) this.autoPlayToggle();
        };
        this.querySelectorAll('.slider-button--prev, .slider-button--next, .slider-counter__link').forEach(
          (control) => control.addEventListener('click', stop)
        );
        this.slider?.addEventListener('touchstart', stop, { passive: true });

        this.querySelectorAll('.banner__promo-chip').forEach((chip) => {
          chip.addEventListener('click', async () => {
            try {
              await navigator.clipboard.writeText(chip.dataset.promoCode);
              const codeEl = chip.querySelector('.banner__promo-code');
              const original = codeEl.textContent;
              codeEl.textContent = 'Copied!';
              setTimeout(() => (codeEl.textContent = original), 1500);
            } catch (e) {
              /* clipboard unavailable (permissions/http) — chip still shows the code */
            }
          });
        });
      }

      ibsCloneOffset() {
        return this.sliderItemsToShow.length * this.sliderItemOffset;
      }

      ibsInstantJump(position) {
        const behavior = this.slider.style.scrollBehavior;
        this.slider.style.scrollBehavior = 'auto';
        this.slider.scrollLeft = position;
        this.slider.style.scrollBehavior = behavior;
      }

      update() {
        if (this.ibsClone && this.sliderItemOffset) {
          const cloneStart = this.ibsCloneOffset();
          if (this.ibsSkipSnap) {
            // mid backward-wrap: we parked on the clone on purpose
            if (this.slider.scrollLeft < cloneStart - 2) this.ibsSkipSnap = false;
          } else if (this.slider.scrollLeft >= cloneStart - 1) {
            // parked on the clone after a forward wrap: swap to the real
            // first slide — identical pixels, so the jump is invisible
            this.ibsInstantJump(0);
          }
        }

        SliderComponent.prototype.update.call(this);

        // Dot state, clamped: mid-wrap the scroll position points one past
        // the last real slide, which the stock update() cannot index.
        this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
        this.prevButton.removeAttribute('disabled');
        if (!this.sliderControlButtons.length) return;
        const page = Math.min(this.currentPage, this.sliderControlButtons.length);
        this.sliderControlButtons.forEach((link) => {
          link.classList.remove('slider-counter__link--active');
          link.removeAttribute('aria-current');
        });
        this.sliderControlButtons[page - 1].classList.add('slider-counter__link--active');
        this.sliderControlButtons[page - 1].setAttribute('aria-current', true);
      }

      onButtonClick(event) {
        if (this.ibsClone) {
          const isLast = this.currentPage >= this.sliderItemsToShow.length;
          const isFirst = this.currentPage === 1;
          if (event.currentTarget.name === 'next' && isLast) {
            event.preventDefault();
            this.setSlidePosition(this.ibsCloneOffset()); // forward, into the clone
            return;
          }
          if (event.currentTarget.name === 'previous' && isFirst) {
            event.preventDefault();
            this.ibsSkipSnap = true;
            this.ibsInstantJump(this.ibsCloneOffset()); // clone === slide 1 pixels
            this.setSlidePosition(this.ibsCloneOffset() - this.sliderItemOffset);
            return;
          }
        }
        super.onButtonClick(event);
      }

      autoRotateSlides() {
        if (!this.ibsClone) return super.autoRotateSlides();
        const atLast = this.currentPage >= this.sliderItemsToShow.length;
        const position = atLast ? this.ibsCloneOffset() : this.slider.scrollLeft + this.sliderItemOffset;
        this.setSlidePosition(position);
      }
    }
  );
}
